import styles from './newMenuDishCard.module.css';
import { useTranslator } from 'hooks/useTranslator';
import { Button } from 'components/common/button';
import { BottomSmall, Close, Complex, Edit, Emergency, Reset, Tag, TopSmall } from 'components/icons';
import { PriceTag } from 'components/common/priceTag/priceTag';
import { Input } from 'components/common/input';
import { ActionGroup } from 'components/common/actionGroup';
import { usePriceFormatter } from 'hooks/useFormattedPrice';
import { useSelectedComplexDish, useSelectedDish } from 'store/newMenu/selectors';
import { useDispatch } from 'react-redux';
import {
    editSelectedComplexDishPrice,
    editSelectedComplexDishSpecialPrice,
    editSelectedDishPrice,
    enableEditSelectedComplex,
    editSelectedDishSpecialPrice,
    resetSelectedComplexDish,
    resetSelectedDish,
} from 'store/newMenu/actions';
import { useApi } from 'hooks/useApi';
import { useState } from 'react';
import { Loader } from 'components/common/loader';
import { IconWrapper } from 'components/common/iconWrapper/iconWrapper';

const SubDish = ({
    dish: { title, id, price: originalPrice, special_price: originalSpecialPrice, allergens, summary },
    complexId,
}) => {
    const dispatch = useDispatch();
    const translator = useTranslator();
    const priceFormatter = usePriceFormatter();
    const { price, special_price } = useSelectedComplexDish(complexId, id);
    const isEdited = price || price === 0;
    const isEditedSpecialPrice = special_price || special_price === 0;

    const noAllergens = !allergens || allergens.length === 0;
    const noSummary = !summary || summary.length === 0;

    return (
        <div className={`${styles.newMenuDishCard__dishCard} standardBg`}>
            <div className={styles.newMenuDishCard__dishCardRow}>
                <div className={styles.newMenuDishCard__dishCardWing}>
                    {noAllergens ? (
                        <IconWrapper color="danger" title="No alergens">
                            <Emergency />
                        </IconWrapper>
                    ) : null}
                    {noSummary ? (
                        <IconWrapper color="danger" title="No summary">
                            <Tag />
                        </IconWrapper>
                    ) : null}
                    {translator(title)}
                </div>
                <div className={styles.newMenuDishCard__dishCardWing}>
                    <div className={styles.newMenuDishCard__priceGroup}>
                        <ActionGroup>
                            <div>Price</div>
                            {isEdited ? (
                                <Button
                                    className={styles.newMenuDishCard__close}
                                    icon={<Reset />}
                                    iconOnly
                                    secondary
                                    small
                                    onClick={() => dispatch(resetSelectedComplexDish(complexId, id))}
                                />
                            ) : (
                                ''
                            )}
                            <PriceTag price={originalPrice} secondary={isEdited} old={isEdited} />
                            <Input
                                id={`new-menu-dish-price-${id}`}
                                type="number"
                                step={0.1}
                                value={price || ''}
                                onChange={event =>
                                    dispatch(editSelectedComplexDishPrice(complexId, id, event.target.value))
                                }
                                min={0}
                                className={styles.newMenuDishCard__priceInput}
                                placeholder={priceFormatter(originalPrice)}
                            />
                        </ActionGroup>
                        {originalSpecialPrice ? (
                            <ActionGroup>
                                <div>Special Price</div>
                                {isEditedSpecialPrice ? (
                                    <Button
                                        className={styles.newMenuDishCard__close}
                                        icon={<Reset />}
                                        iconOnly
                                        secondary
                                        small
                                        onClick={() => dispatch(resetSelectedComplexDish(complexId, id))}
                                    />
                                ) : (
                                    ''
                                )}
                                <PriceTag
                                    price={originalSpecialPrice}
                                    secondary={isEditedSpecialPrice}
                                    old={isEditedSpecialPrice}
                                />
                                <Input
                                    id={`new-menu-dish-special-price-${id}`}
                                    type="number"
                                    step={0.1}
                                    value={special_price || ''}
                                    onChange={event =>
                                        dispatch(editSelectedComplexDishSpecialPrice(complexId, id, event.target.value))
                                    }
                                    min={0}
                                    className={styles.newMenuDishCard__priceInput}
                                    placeholder={priceFormatter(originalSpecialPrice)}
                                />
                            </ActionGroup>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const NewMenuDishCard = ({ dish, onRemove }) => {
    const dispatch = useDispatch();
    const translator = useTranslator();
    const { type, price: originalPrice, special_price: originalSpecialPrice, title, id, allergens, summary } = dish;
    const priceFormatter = usePriceFormatter();
    const { price, special_price, complex } = useSelectedDish(id);
    const [loading, setLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [complexDish, setComplexDish] = useState(null);
    const requestComplex = useApi('/dishes', { parsed: true });
    const isEdited = price || price === 0;
    const isEditedSpecialPrice = special_price || special_price === 0;

    const handleEdit = async () => {
        setLoading(true);
        const result = await requestComplex({ route: `/${id}` });
        setLoading(false);
        setComplexDish(result);
        dispatch(enableEditSelectedComplex(id));
    };

    const noAllergens = !allergens || allergens.length === 0;
    const noSummary = !summary || summary.length === 0;

    return (
        <div className={`${styles.newMenuDishCard} shaded`}>
            <div className={styles.newMenuDishCard__header}>
                <div className={styles.newMenuDishCard__headerWing}>
                    {noAllergens && type !== 'complex' ? (
                        <IconWrapper color="danger" title="No allergens">
                            <Emergency />
                        </IconWrapper>
                    ) : null}
                    {noSummary ? (
                        <IconWrapper color="danger" title="No summary">
                            <Tag />
                        </IconWrapper>
                    ) : null}
                    {type === 'complex' ? (
                        <div className={styles.newMenuDishCard__icon}>
                            <Complex />
                        </div>
                    ) : (
                        ''
                    )}
                    <div className={styles.newMenuDishCard__title}>{translator(title)}</div>
                </div>
                <div className={styles.newMenuDishCard__headerWing}>
                    <ActionGroup>
                        <Button
                            icon={<Reset />}
                            iconOnly
                            secondary={
                                !(
                                    isEdited ||
                                    isEditedSpecialPrice ||
                                    (complex && complex.some(item => item.price || item.special_price))
                                )
                            }
                            small
                            onClick={() => dispatch(resetSelectedDish(id))}
                        />
                        {type !== 'complex' ? (
                            <div className={styles.newMenuDishCard__priceGroup}>
                                <ActionGroup slim>
                                    <div>Price</div>
                                    <PriceTag price={originalPrice} secondary={isEdited} old={isEdited} />
                                    <Input
                                        id={`new-menu-dish-price-${id}`}
                                        type="number"
                                        step={0.1}
                                        value={price === 0 ? 0 : price || ''}
                                        onChange={event => dispatch(editSelectedDishPrice(id, event.target.value))}
                                        min={0}
                                        className={styles.newMenuDishCard__priceInput}
                                        placeholder={priceFormatter(originalPrice)}
                                    />
                                </ActionGroup>
                                {originalSpecialPrice ? (
                                    <ActionGroup slim>
                                        <div>Special Price</div>
                                        <PriceTag
                                            price={originalSpecialPrice}
                                            secondary={isEditedSpecialPrice}
                                            old={isEditedSpecialPrice}
                                        />
                                        <Input
                                            id={`new-menu-dish-special-price-${id}`}
                                            type="number"
                                            step={0.1}
                                            value={special_price === 0 ? 0 : special_price || ''}
                                            onChange={event =>
                                                dispatch(editSelectedDishSpecialPrice(id, event.target.value))
                                            }
                                            min={0}
                                            className={styles.newMenuDishCard__priceInput}
                                            placeholder={priceFormatter(originalSpecialPrice)}
                                        />
                                    </ActionGroup>
                                ) : null}
                            </div>
                        ) : !complexDish ? (
                            <Button
                                icon={<Edit />}
                                iconOnly
                                secondary
                                disabled={loading || complexDish}
                                small
                                onClick={handleEdit}
                            />
                        ) : (
                            <Button
                                icon={collapsed ? <BottomSmall /> : <TopSmall />}
                                iconOnly
                                secondary
                                small
                                onClick={() => setCollapsed(!collapsed)}
                            />
                        )}
                        <Button icon={<Close />} iconOnly secondary small onClick={onRemove} />
                    </ActionGroup>
                </div>
            </div>
            {(loading || (complex && !complexDish && loading) || complexDish) && !collapsed ? (
                <div className={styles.newMenuDishCard__body}>
                    {loading && !complexDish ? (
                        <Loader />
                    ) : (
                        complexDish?.complex.map((complexCategory, index) => {
                            return (
                                <div className={styles.newMenuDishCard__complexCategory} key={index}>
                                    <div className={styles.newMenuDishCard__complexCategoryTitle}>
                                        {translator(complexCategory.title)}
                                    </div>
                                    <div className={styles.newMenuDishCard__dishGrid}>
                                        {complexCategory.dishes.map(subDish => (
                                            <SubDish dish={subDish} complexId={id} key={subDish.id} />
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            ) : (
                ''
            )}
        </div>
    );
};
